import React, { useEffect, useState } from 'react';
import { Typography, Box, Card, Radio, Divider, Button, CircularProgress, RadioGroup, Icon } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { AddOutlined } from '@mui/icons-material';
import { getUserFromLocalStorage } from '../../../utils/utils';
import Axios from '../../../config/axios';
import { UrlConstant } from '../../../commom/UrlConstant';
import Successpopup from '../SharedComponent/Successpopup';
import checked from '../../../commom/Assets/images/CheckedIcon.png';
import unchecked from '../../../commom/Assets/images/Unchecked.png';
import aeropayLogo from '../../../style/images/aeroPaylogo.png';
import OtpVerificationDialog from '../Dialogs/Otpverificationdialog';
import AeroSyncWidgetModal from '../SharedComponent/Aerosyncwidgetmodal';
import useAeroPayBankLink from '../../../hooks/useaeropaybanklink';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

interface Account {
    id: string;
    bankName?: string;
    bankAccountType?: string;
    type?: string;
    status: string;
    isDefault?: boolean;
    accountLast4?: string;
    connectionId?: string;
}

function LinkAccountSimplified() {
    const location = useLocation();
    const navigation = useNavigate();
    const user = getUserFromLocalStorage();

    const [loading, setLoading] = useState<boolean>(false);
    const [approvedAccounts, setApprovedAccounts] = useState<Account[]>([]);
    const [pendingAccounts, setPendingAccounts] = useState<Account[]>([]);
    const [cancelledAccounts, setCancelledAccounts] = useState<Account[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

    // Popup states
    const [showPopup, setShowPopup] = useState(false);
    const [msg, setMsg] = useState('');
    const [success, setSuccess] = useState(false);
    const [heading, setHeading] = useState('');

    const checkExistingAccounts = async (): Promise<boolean> => {
        setLoading(true);
        try {
            const response = await Axios.get(UrlConstant.AeroPay.Getbankdetails);
            const resData = response.data;
            if (resData.status === true || resData.success === true || resData.status === 'success') {
                const accountList: Account[] =
                    resData.data?.bankAccounts?.map((acc: any) => ({
                        id: acc.bankAccountId,
                        bankName: acc.bankName,
                        type: acc.accountType,
                        status: acc.status,
                        isDefault: acc.isSelected,
                        connectionId: acc.connectionId,
                        accountLast4: acc.accountLast4,
                        bankAccountType: acc.name,
                    })) ?? [];

                setApprovedAccounts(accountList.filter(
                    (a) => a.status === 'verified' || a.status === 'active'
                ));
                setPendingAccounts(accountList.filter((a) => a.status === 'PENDING'));
                setCancelledAccounts(accountList.filter((a) => a.status === 'FAILED'));

                return accountList.length > 0;
            }
            return false;
        } catch (error: any) {
            console.error('Error checking accounts:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Use the AeroPay hook
    const {
        isWidgetOpen,
        isLinking,
        isLoading: isBankLoading,
        needsOtpVerification,
        otp,
        isVerifyingOtp,
        // widgetUrl,
        error: bankError,
        startBankLink,
        handleOtpVerification,
        cancelOtpVerification,
        closeWidget,
        setOtp,
        isAeroPayUser,
    } = useAeroPayBankLink({
        onSuccess: (connectionId) => {
            setHeading('Success');
            setSuccess(true);
            setMsg('Bank account linked successfully!');
            setShowPopup(true);
            getLinkedAccounts();
        },
        onError: (error) => {
            setHeading('Error');
            setSuccess(false);
            setMsg(error);
            setShowPopup(true);
        },
        onAfterOtpVerified: checkExistingAccounts,
    });

    const onSetDefault = async (accountId: string) => {
        try {
            setLoading(true);
            await Axios.post(UrlConstant.AeroPay.changeDefaultBank, {
                bankAccountId: accountId,
            });
            await getLinkedAccounts();
        } catch (error) {
            console.error('Error in Set Default:', error);
            setHeading('Error');
            setShowPopup(true);
            setMsg('Failed to set default account');
        } finally {
            setLoading(false);
        }
    };

    const getLinkedAccounts = async () => {
        setLoading(true);

        try {
            const response = await Axios.get(UrlConstant.AeroPay.Getbankdetails);
            const resData = response.data;
            if (resData.status === true || resData.success === true || resData.status === 'success') {
                const accountList: Account[] =
                    resData.data?.bankAccounts?.map((acc: any) => ({
                        id: acc.bankAccountId,
                        bankName: acc.bankName,
                        type: acc.accountType,
                        status: acc.status,
                        isDefault: acc.isSelected,
                        connectionId: acc.connectionId,
                        accountLast4: acc.accountLast4,
                        bankAccountType: acc.name
                    })) ?? [];

                const approved = accountList.filter(
                    (account) => account.status === 'verified' || account.status === 'active'
                );
                const pending = accountList.filter((account) => account.status === 'PENDING');
                const cancelled = accountList.filter((account) => account.status === 'FAILED');
                setApprovedAccounts(approved);
                setPendingAccounts(pending);
                setCancelledAccounts(cancelled);
            }
        } catch (error: any) {
            console.error('Error: ', error);
            const isNoDataError = error?.response?.data?.code === 'INTERNAL_ERROR';
            if (isNoDataError && !isAeroPayUser) {
                // Show onboarding popup or handle new user flow
            } else if (!isNoDataError) {
                setHeading('Error');
                setShowPopup(true);
                setMsg('An error occurred while fetching linked accounts.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAeroPayUser) {
            getLinkedAccounts();
        }
    }, [isAeroPayUser]);

    const closePopup = () => {
        setShowPopup(false);
    };

    const getDisplayName = (account: Account) => account.bankName || 'Bank Account';
    const getAccountType = (account: Account) => account.bankAccountType || account.type || 'Bank';

    return (
        <Box
            sx={{ minHeight: 'calc(100vh - 71px - 143px )', display: 'flex', flexDirection: 'column' }}
            className="bg-dak"
        >
            <Box sx={{ padding: '30px', flexGrow: 1 }}>
                <Typography className="text-secondary" sx={{ marginBottom: '20px' }}>
                    <span onClick={() => navigation(-1)} style={{ color: '#4A7AF2', cursor: 'pointer' }}>
                        More Options{' '}
                    </span>{' '}
                    / Link Bank Accounts
                </Typography>

                {loading && (
                    <Box
                        position="fixed"
                        top={0}
                        left={0}
                        width="100vw"
                        height="100vh"
                        bgcolor="rgba(0, 0, 0, 0.5)"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        style={{
                            backdropFilter: 'blur(5px)',
                            zIndex: 9999,
                        }}
                    >
                        <CircularProgress color="primary" />
                    </Box>
                )}

                {/* Approved/Active Accounts */}
                <Box width="100%" mb="50px">
                    {isAeroPayUser ? (
                        <Box display="flex" alignItems="center" mb="20px" justifyContent="space-between">
                            <Typography variant="h5" className="text-white">
                                Linked Bank Accounts
                            </Typography>
                            <Box className="d-flex" gap={2}>
                            <Button
                                sx={{ padding: '5px 10px' }}
                                className="btn-primary btn"
                                startIcon={<AddOutlined />}
                                onClick={startBankLink}
                                disabled={isBankLoading || loading}
                            >
                                {isBankLoading ? 'Loading...' : 'Add Bank Account'}
                            </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box display="flex w-100">
                            <Box className="w-100">
                                <Typography variant="h5" className="text-white" sx={{ fontWeight: 500, mb: 1 }}>
                                    Link Your Bank Account
                                </Typography>
                                <Box
                                    className="w-100"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    gap={2}
                                    mb={3}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: '#1e1e1e',
                                            borderRadius: '12px',
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <svg width="226" height="50" viewBox="0 0 126 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.29097 19.9558C6.2701 20.1033 6.25967 20.2521 6.25977 20.401C6.25977 22.3891 8.09853 24 10.3671 24C12.6356 24 14.4746 22.3885 14.4746 20.401C14.4746 20.2521 14.4642 20.1033 14.4434 19.9558C14.0389 21.5902 13.0024 22.7531 11.7874 22.7531C11.27 22.7531 10.7849 22.542 10.3671 22.1737C9.94949 22.542 9.46441 22.7531 8.94699 22.7531C7.73203 22.7531 6.69549 21.5902 6.29097 19.9558Z" fill="url(#paint0_linear_442_396)"></path><path d="M18.0214 5.31398C18.0028 5.90141 17.9171 6.48489 17.7661 7.05339C17.7193 7.22867 17.7054 7.14579 17.7094 7.08139C17.7533 6.36849 17.5485 5.69675 17.2237 5.13226C17.115 5.46088 16.9573 5.77165 16.7557 6.05432C16.7767 5.64466 16.7223 5.23463 16.5951 4.84413C16.6156 4.92815 16.6243 5.01454 16.6209 5.1009C16.605 5.58587 16.2181 5.96556 15.7569 5.94848C15.2956 5.9314 14.9342 5.52455 14.951 5.03957C14.9606 4.74893 15.0928 4.47956 15.3158 4.34376C16.1639 3.83254 16.9318 3.20184 17.5953 2.47163C17.9082 3.38605 18.0523 4.34847 18.0209 5.31314L18.0214 5.31398ZM5.78351 5.03818C5.79968 5.52315 5.43885 5.93028 4.97788 5.94708C4.51691 5.96388 4.13027 5.58531 4.11523 5.10034C4.11212 5.01398 4.1209 4.9276 4.14133 4.84357C4.01389 5.23402 3.95939 5.64408 3.98049 6.05376C3.77869 5.77108 3.62065 5.46032 3.51158 5.1317C3.18649 5.69619 2.98196 6.36793 3.02593 7.08083C3.0299 7.14579 3.016 7.22867 2.9692 7.05283C2.81819 6.48433 2.73254 5.90085 2.71389 5.31342C2.68254 4.34876 2.82666 3.38636 3.1394 2.47191C3.80333 3.20191 4.57167 3.83232 5.42013 4.3432C5.64253 4.47872 5.775 4.74837 5.78465 5.03902L5.78351 5.03818ZM18.5961 7.4608C18.8012 6.71754 18.9044 5.95047 18.9028 5.18014C18.9028 3.22317 18.2566 1.42496 17.1724 0C15.8091 2.45623 13.2722 4.11415 10.3671 4.11415C7.462 4.11415 4.9254 2.45623 3.56207 0C2.47845 1.42496 1.83167 3.22317 1.83167 5.18014C1.8302 5.95049 1.93344 6.71755 2.13861 7.4608C1.01498 8.30894 0.221264 9.61686 0 11.1255C0.424821 10.9913 0.865606 10.9125 1.31113 10.8912C0.723463 11.8149 0.414044 12.8845 0.418985 13.9752C0.418985 15.4273 0.948032 16.7495 1.80614 17.7178C2.03336 17.2733 2.30052 16.8498 2.6044 16.4524C2.78808 17.6932 3.24681 18.8787 3.94787 19.9245C4.06202 20.1031 4.18657 20.275 4.3209 20.4394C4.56023 19.6596 4.97147 18.9418 5.52509 18.3374C5.2169 18.4072 4.90226 18.4456 4.58613 18.4519C4.13445 17.6091 3.89977 16.6695 3.90276 15.716C3.90214 15.1297 3.99 14.5466 4.16346 13.9858C3.47299 14.1418 2.82325 14.4387 2.25605 14.8575C2.21729 14.5827 2.19786 14.3055 2.19789 14.0281C2.19789 10.9573 4.04885 9.49197 6.20505 9.49197C10.4698 9.49197 9.10248 16.0601 8.44748 18.163C9.6814 17.6849 11.0528 17.6849 12.2867 18.163C11.6326 16.0598 10.2647 9.4917 14.5294 9.4917C16.6853 9.4917 18.5366 10.957 18.5366 14.0278C18.5366 14.3053 18.5172 14.5824 18.4784 14.8572C17.9112 14.4384 17.2615 14.1415 16.571 13.9855C16.7445 14.5463 16.8323 15.1294 16.8317 15.7157C16.8345 16.669 16.5997 17.6084 16.1481 18.4511C15.8319 18.4448 15.5173 18.4064 15.2091 18.3366C15.7627 18.9409 16.1739 19.6588 16.4133 20.4386C16.5474 20.2742 16.6719 20.1024 16.786 19.9239C17.4871 18.8782 17.946 17.6927 18.1298 16.4518C18.4337 16.8492 18.7008 17.2727 18.928 17.7172C19.7861 16.7489 20.3152 15.4267 20.3152 13.9746C20.32 12.8839 20.0106 11.8144 19.423 10.8906C19.8686 10.912 20.3094 10.9908 20.7342 11.125C20.5129 9.61574 19.7189 8.30838 18.5956 7.46024" fill="url(#paint1_linear_442_396)"></path><path d="M6.91138 11.7678C6.91138 11.7678 6.98485 11.8264 7.06002 11.8882C6.91053 12.3242 6.61636 12.5603 6.18064 12.5603C5.59287 12.5603 5.27289 12.1123 5.34352 11.5242C5.34966 11.448 5.36755 11.3731 5.39657 11.3022C5.44054 11.2997 5.48479 11.2983 5.52905 11.2983C5.61892 11.2983 5.70872 11.3035 5.79797 11.314C5.80024 11.3193 5.80137 11.3226 5.80137 11.3226C5.54862 11.8359 6.02235 12.2117 6.02235 12.2117C6.02235 12.2117 6.23227 12.1666 6.49949 11.9876C6.65267 11.9011 6.706 11.6637 6.91081 11.7692L6.91138 11.7678ZM7.24186 11.7636C6.49353 11.0076 5.23403 10.8528 4.32003 11.3982L4.30188 11.4086C4.4331 11.8213 4.6753 12.191 5.00267 12.4785C5.33003 12.766 5.73029 12.9604 6.16078 13.041L6.15936 13.0461C6.85011 13.1432 7.49262 13.5641 7.86679 14.1101C8.10961 13.2928 7.86196 12.3559 7.24271 11.7631" fill="url(#paint2_linear_442_396)"></path><path d="M13.8227 11.7678C13.8227 11.7678 13.7493 11.8264 13.6744 11.8882C13.8239 12.3242 14.118 12.5603 14.5538 12.5603C15.1412 12.5603 15.4615 12.1123 15.3909 11.5242C15.3847 11.448 15.3668 11.3731 15.3378 11.3022C15.2939 11.2997 15.2496 11.2983 15.2051 11.2983C15.1153 11.2983 15.0256 11.3035 14.9364 11.314L14.9327 11.3226C15.1858 11.8359 14.712 12.2117 14.712 12.2117C14.712 12.2117 14.5021 12.1666 14.2349 11.9876C14.0817 11.9011 14.0281 11.6637 13.8233 11.7692L13.8227 11.7678ZM13.4925 11.7636C14.2406 11.0076 15.5004 10.8528 16.4144 11.3982L16.4322 11.4086C16.3011 11.8213 16.059 12.1911 15.7317 12.4786C15.4043 12.766 15.0041 12.9605 14.5736 13.041V13.0461C13.8829 13.1432 13.2403 13.5641 12.8665 14.1101C12.6236 13.2928 12.8713 12.3559 13.4905 11.7631" fill="url(#paint3_linear_442_396)"></path><path d="M12.0371 20.0015C12.0371 19.2914 11.2896 18.9717 10.3671 18.9717C9.44462 18.9717 8.69714 19.2914 8.69714 20.0015C8.69739 20.0465 8.70052 20.0914 8.7065 20.136C8.79161 20.752 9.47753 21.0454 10.3671 21.4677C11.2567 21.0454 11.9421 20.752 12.028 20.136C12.0339 20.0914 12.037 20.0465 12.0371 20.0015Z" fill="url(#paint4_linear_442_396)"></path><path d="M6.29097 19.9558C6.2701 20.1033 6.25967 20.2521 6.25977 20.401C6.25977 22.3891 8.09853 24 10.3671 24C12.6356 24 14.4746 22.3885 14.4746 20.401C14.4746 20.2521 14.4642 20.1033 14.4434 19.9558C14.0389 21.5902 13.0024 22.7531 11.7874 22.7531C11.27 22.7531 10.7849 22.542 10.3671 22.1737C9.94949 22.542 9.46441 22.7531 8.94699 22.7531C7.73203 22.7531 6.69549 21.5902 6.29097 19.9558Z" fill="url(#paint5_linear_442_396)"></path><path d="M18.0214 5.31398C18.0028 5.90141 17.9171 6.48489 17.7661 7.05339C17.7193 7.22867 17.7054 7.14579 17.7094 7.08139C17.7533 6.36849 17.5485 5.69675 17.2237 5.13226C17.115 5.46088 16.9573 5.77165 16.7557 6.05432C16.7767 5.64466 16.7223 5.23463 16.5951 4.84413C16.6156 4.92815 16.6243 5.01454 16.6209 5.1009C16.605 5.58587 16.2181 5.96556 15.7569 5.94848C15.2956 5.9314 14.9342 5.52455 14.951 5.03957C14.9606 4.74893 15.0928 4.47956 15.3158 4.34376C16.1639 3.83254 16.9318 3.20184 17.5953 2.47163C17.9082 3.38605 18.0523 4.34847 18.0209 5.31314L18.0214 5.31398ZM5.78351 5.03818C5.79968 5.52315 5.43885 5.93028 4.97788 5.94708C4.51691 5.96388 4.13027 5.58531 4.11523 5.10034C4.11212 5.01398 4.1209 4.9276 4.14133 4.84357C4.01389 5.23402 3.95939 5.64408 3.98049 6.05376C3.77869 5.77108 3.62065 5.46032 3.51158 5.1317C3.18649 5.69619 2.98196 6.36793 3.02593 7.08083C3.0299 7.14579 3.016 7.22867 2.9692 7.05283C2.81819 6.48433 2.73254 5.90085 2.71389 5.31342C2.68254 4.34876 2.82666 3.38636 3.1394 2.47191C3.80333 3.20191 4.57167 3.83232 5.42013 4.3432C5.64253 4.47872 5.775 4.74837 5.78465 5.03902L5.78351 5.03818ZM18.5961 7.4608C18.8012 6.71754 18.9044 5.95047 18.9028 5.18014C18.9028 3.22317 18.2566 1.42496 17.1724 0C15.8091 2.45623 13.2722 4.11415 10.3671 4.11415C7.462 4.11415 4.9254 2.45623 3.56207 0C2.47845 1.42496 1.83167 3.22317 1.83167 5.18014C1.8302 5.95049 1.93344 6.71755 2.13861 7.4608C1.01498 8.30894 0.221264 9.61686 0 11.1255C0.424821 10.9913 0.865606 10.9125 1.31113 10.8912C0.723463 11.8149 0.414044 12.8845 0.418985 13.9752C0.418985 15.4273 0.948032 16.7495 1.80614 17.7178C2.03336 17.2733 2.30052 16.8498 2.6044 16.4524C2.78808 17.6932 3.24681 18.8787 3.94787 19.9245C4.06202 20.1031 4.18657 20.275 4.3209 20.4394C4.56023 19.6596 4.97147 18.9418 5.52509 18.3374C5.2169 18.4072 4.90226 18.4456 4.58613 18.4519C4.13445 17.6091 3.89977 16.6695 3.90276 15.716C3.90214 15.1297 3.99 14.5466 4.16346 13.9858C3.47299 14.1418 2.82325 14.4387 2.25605 14.8575C2.21729 14.5827 2.19786 14.3055 2.19789 14.0281C2.19789 10.9573 4.04885 9.49197 6.20505 9.49197C10.4698 9.49197 9.10248 16.0601 8.44748 18.163C9.6814 17.6849 11.0528 17.6849 12.2867 18.163C11.6326 16.0598 10.2647 9.4917 14.5294 9.4917C16.6853 9.4917 18.5366 10.957 18.5366 14.0278C18.5366 14.3053 18.5172 14.5824 18.4784 14.8572C17.9112 14.4384 17.2615 14.1415 16.571 13.9855C16.7445 14.5463 16.8323 15.1294 16.8317 15.7157C16.8345 16.669 16.5997 17.6084 16.1481 18.4511C15.8319 18.4448 15.5173 18.4064 15.2091 18.3366C15.7627 18.9409 16.1739 19.6588 16.4133 20.4386C16.5474 20.2742 16.6719 20.1024 16.786 19.9239C17.4871 18.8782 17.946 17.6927 18.1298 16.4518C18.4337 16.8492 18.7008 17.2727 18.928 17.7172C19.7861 16.7489 20.3152 15.4267 20.3152 13.9746C20.32 12.8839 20.0106 11.8144 19.423 10.8906C19.8686 10.912 20.3094 10.9908 20.7342 11.125C20.5129 9.61574 19.7189 8.30838 18.5956 7.46024" fill="url(#paint6_linear_442_396)"></path><path d="M6.91138 11.7678C6.91138 11.7678 6.98485 11.8264 7.06002 11.8882C6.91053 12.3242 6.61636 12.5603 6.18064 12.5603C5.59287 12.5603 5.27289 12.1123 5.34352 11.5242C5.34966 11.448 5.36755 11.3731 5.39657 11.3022C5.44054 11.2997 5.48479 11.2983 5.52905 11.2983C5.61892 11.2983 5.70872 11.3035 5.79797 11.314C5.80024 11.3193 5.80137 11.3226 5.80137 11.3226C5.54862 11.8359 6.02235 12.2117 6.02235 12.2117C6.02235 12.2117 6.23227 12.1666 6.49949 11.9876C6.65267 11.9011 6.706 11.6637 6.91081 11.7692L6.91138 11.7678ZM7.24186 11.7636C6.49353 11.0076 5.23403 10.8528 4.32003 11.3982L4.30188 11.4086C4.4331 11.8213 4.6753 12.191 5.00267 12.4785C5.33003 12.766 5.73029 12.9604 6.16078 13.041L6.15936 13.0461C6.85011 13.1432 7.49262 13.5641 7.86679 14.1101C8.10961 13.2928 7.86196 12.3559 7.24271 11.7631" fill="url(#paint7_linear_442_396)"></path><path d="M13.8227 11.7678C13.8227 11.7678 13.7493 11.8264 13.6744 11.8882C13.8239 12.3242 14.118 12.5603 14.5538 12.5603C15.1412 12.5603 15.4615 12.1123 15.3909 11.5242C15.3847 11.448 15.3668 11.3731 15.3378 11.3022C15.2939 11.2997 15.2496 11.2983 15.2051 11.2983C15.1153 11.2983 15.0256 11.3035 14.9364 11.314L14.9327 11.3226C15.1858 11.8359 14.712 12.2117 14.712 12.2117C14.712 12.2117 14.5021 12.1666 14.2349 11.9876C14.0817 11.9011 14.0281 11.6637 13.8233 11.7692L13.8227 11.7678ZM13.4925 11.7636C14.2406 11.0076 15.5004 10.8528 16.4144 11.3982L16.4322 11.4086C16.3011 11.8213 16.059 12.1911 15.7317 12.4786C15.4043 12.766 15.0041 12.9605 14.5736 13.041V13.0461C13.8829 13.1432 13.2403 13.5641 12.8665 14.1101C12.6236 13.2928 12.8713 12.3559 13.4905 11.7631" fill="url(#paint8_linear_442_396)"></path><path d="M12.0371 20.0015C12.0371 19.2914 11.2896 18.9717 10.3671 18.9717C9.44462 18.9717 8.69714 19.2914 8.69714 20.0015C8.69739 20.0465 8.70052 20.0914 8.7065 20.136C8.79161 20.752 9.47753 21.0454 10.3671 21.4677C11.2567 21.0454 11.9421 20.752 12.028 20.136C12.0339 20.0914 12.037 20.0465 12.0371 20.0015Z" fill="url(#paint9_linear_442_396)"></path><path d="M126 18.1976L120.51 12.1682L125.071 6.5953H121.895L117.969 11.4489V6.5953H115.395V18.2002H117.969V13.0036L122.677 18.2002L126 18.1976ZM110.86 17.2445C111.899 16.5224 112.638 15.4439 112.938 14.2127H110.12C109.88 14.7354 109.48 15.1677 108.979 15.4466C108.473 15.7348 107.9 15.8843 107.318 15.8801C106.276 15.8801 105.437 15.5476 104.801 14.8825C104.164 14.2175 103.847 13.3898 103.848 12.3994C103.848 11.4079 104.165 10.5799 104.801 9.91549C105.436 9.25106 106.275 8.91884 107.318 8.91884C107.899 8.91489 108.472 9.06405 108.979 9.35146C109.48 9.63082 109.88 10.063 110.12 10.5853H112.938C112.637 9.35445 111.899 8.27633 110.86 7.55441C109.833 6.80028 108.591 6.39654 107.318 6.40245C105.569 6.40245 104.119 6.97986 102.968 8.13466C101.818 9.28946 101.242 10.7116 101.241 12.4012C101.241 14.0896 101.817 15.5117 102.968 16.6677C104.119 17.8236 105.569 18.401 107.318 18.3999C108.591 18.4048 109.833 17.9998 110.86 17.2445ZM95.669 13.49H92.753L94.2195 10.0094L95.669 13.49ZM90.7983 18.1959L91.825 15.7444H96.614L97.6399 18.1959H100.344L95.3925 6.59104H93.0465L88.0941 18.1959H90.7983ZM84.6397 11.6434C84.4884 11.7894 84.3096 11.9036 84.1137 11.9794C83.9178 12.0551 83.7088 12.0909 83.499 12.0846H82.1924V9.04513H83.4956C83.9629 9.04513 84.3456 9.18933 84.6439 9.47775C84.7866 9.61967 84.8997 9.78864 84.9766 9.97481C85.0536 10.161 85.0928 10.3607 85.0921 10.5622C85.0914 10.7637 85.0507 10.9631 84.9725 11.1487C84.8942 11.3344 84.7799 11.5025 84.6363 11.6434H84.6397ZM83.5305 6.5936H79.5997V18.1985H82.189V14.5216H83.5254C84.6873 14.5216 85.6565 14.143 86.4328 13.3859C87.2092 12.6287 87.5973 11.6901 87.5973 10.57C87.5973 9.4476 87.2092 8.50528 86.4328 7.743C85.6565 6.98071 84.6873 6.59929 83.5254 6.59872L83.5305 6.5936ZM72.4629 18.2036V13.6606H76.0058V11.1767H72.4663V9.08523H76.995V6.5953H69.8761V18.2002L72.4629 18.2036ZM67.4118 18.2036V15.7196H62.6296V6.5953H60.036V18.2002L67.4118 18.2036ZM57.3931 12.4012C57.3931 10.756 56.8092 9.34521 55.6416 8.16879C54.474 6.99238 53.065 6.40416 51.4148 6.40416C49.764 6.40416 48.3548 6.98953 47.1872 8.16026C46.0195 9.33099 45.436 10.7446 45.4366 12.4012C45.4366 14.0566 46.0201 15.4699 47.1872 16.6412C48.3542 17.8125 49.7634 18.3982 51.4148 18.3982C53.065 18.3982 54.474 17.8099 55.6416 16.6335C56.8092 15.4571 57.3931 14.0463 57.3931 12.4012ZM48.0429 12.4012C48.0367 11.9474 48.1206 11.4969 48.2897 11.0761C48.4588 10.6552 48.7098 10.2722 49.0279 9.94963C49.3344 9.62478 49.7037 9.36603 50.1132 9.1892C50.5227 9.01237 50.9639 8.92117 51.4097 8.92117C51.8555 8.92117 52.2967 9.01237 52.7062 9.1892C53.1158 9.36603 53.485 9.62478 53.7915 9.94963C54.4189 10.6105 54.7689 11.4881 54.7689 12.4007C54.7689 13.3133 54.4189 14.191 53.7915 14.8518C53.4852 15.177 53.116 15.4361 52.7065 15.6131C52.2969 15.7901 51.8557 15.8815 51.4097 15.8815C50.9637 15.8815 50.5225 15.7901 50.1129 15.6131C49.7034 15.4361 49.3342 15.177 49.0279 14.8518C48.7096 14.5295 48.4585 14.1468 48.2894 13.726C48.1203 13.3052 48.0365 12.8548 48.0429 12.4012ZM41.2166 18.2036L43.9046 6.59872H41.1375L39.5578 13.9525L36.3025 6.3999L33.0607 13.949L31.4802 6.5953H28.7089L31.3799 18.2002H33.8399L36.3025 12.5274L38.7616 18.1976L41.2166 18.2036Z" fill="white"></path><defs><linearGradient id="paint0_linear_442_396" x1="5.74634" y1="19.9558" x2="5.86328" y2="24.2353" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint1_linear_442_396" x1="-1.29589" y1="-3.70057e-09" x2="-0.114988" y2="21.5803" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint2_linear_442_396" x1="4.07332" y1="11.0735" x2="4.22123" y2="14.2824" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint3_linear_442_396" x1="12.5458" y1="11.0735" x2="12.6937" y2="14.2824" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint4_linear_442_396" x1="8.4884" y1="18.9717" x2="8.59786" y2="21.6103" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint5_linear_442_396" x1="5.74634" y1="19.9558" x2="5.86328" y2="24.2353" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint6_linear_442_396" x1="-1.29589" y1="-3.70057e-09" x2="-0.114988" y2="21.5803" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint7_linear_442_396" x1="4.07332" y1="11.0735" x2="4.22123" y2="14.2824" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint8_linear_442_396" x1="12.5458" y1="11.0735" x2="12.6937" y2="14.2824" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient><linearGradient id="paint9_linear_442_396" x1="8.4884" y1="18.9717" x2="8.59786" y2="21.6103" gradientUnits="userSpaceOnUse"><stop stop-color="#F2674A"></stop><stop offset="1" stop-color="#FCB118"></stop></linearGradient></defs></svg>
                                    </Box>
                                    {/* <Button fontWeight={'bold'} sx={{ color: '#ffffff', fontSize: 20 }}> */}
                                        <SwapHorizIcon />
                                    {/* </Button> */}
                                    <Box
                                        sx={{
                                            bgcolor: '#1e1e1e',
                                            borderRadius: '12px',
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <img
                                            src={aeropayLogo}
                                            alt="Aeropay"
                                            style={{ height: 35 }}
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Box gap={3} className="d-flex align-items-center flex-column justify-content-between mb-3">
                                    <Typography variant="h6" className="text-center" sx={{ color: '#ffffff99' }}>
                                        Connect your bank account securely via Aeropay to fund your Wolfpack account and
                                        start investing.
                                        </Typography>
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            gap={3}
                                            flexWrap="wrap"
                                        >
                                            <Typography
                                                component="a"
                                                href="https://www.aeropay.com/legal/integration-agreement"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: '#4A7AF2',
                                                    fontSize: '14px',
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' },
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Integration Agreement
                                            </Typography>

                                            <Typography sx={{ color: '#ffffff55' }}>|</Typography>

                                            <Typography
                                                component="a"
                                                href="https://www.aeropay.com/legal/terms-of-service---end-user"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: '#4A7AF2',
                                                    fontSize: '14px',
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' },
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Terms Of Service
                                            </Typography>

                                            <Typography sx={{ color: '#ffffff55' }}>|</Typography>

                                            <Typography
                                                component="a"
                                                href="https://www.aeropay.com/legal/privacy-policy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: '#4A7AF2',
                                                    fontSize: '14px',
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' },
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Privacy Policy
                                            </Typography>
                                        </Box>
                                    <Button
                                        className="btn-primary btn"
                                        onClick={startBankLink}
                                        disabled={isBankLoading}
                                        sx={{ borderRadius: '50px', py: 1, px: 4, textTransform: 'none', fontSize: 16 }}
                                    >
                                        {isBankLoading ? (
                                            <CircularProgress size={22} sx={{ color: '#fff' }} />
                                        ) : (
                                            'Get Started'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {approvedAccounts.length ? (
                        <RadioGroup
                            className="w-100 d-flex"
                            style={{ gap: '20px', flexDirection: 'row' }}
                            value={selectedAccountId}
                            onChange={(e) => {
                                const clickedId = e.target.value;
                                const clickedAccount = approvedAccounts.find((a) => a.id === clickedId);
                                if (!clickedAccount?.isDefault) {
                                    setSelectedAccountId(clickedId);
                                    onSetDefault(clickedId);
                                }
                            }}
                        >
                            {approvedAccounts.map((account: Account) => (
                                <Box width="49%" key={account.id}>
                                    <Card sx={{ height: '113px', width: '100% !important' }} className="card">
                                        <Box display="flex" height="100%" width="100%">
                                            <Radio
                                                className="mx-3"
                                                checked={account?.isDefault || selectedAccountId === account.id}
                                                value={account.id}
                                                icon={
                                                    <img
                                                        src={unchecked}
                                                        alt="unchecked"
                                                        style={{ width: 40, height: 40 }}
                                                    />
                                                }
                                                checkedIcon={
                                                    <img src={checked} alt="checked" style={{ width: 48, height: 48 }} />
                                                }
                                                sx={{
                                                    color: '#4A7AF2',
                                                    '&.Mui-checked': { color: '#4A7AF2' },
                                                }}
                                            />
                                            <Divider sx={{ border: '1px solid white' }} />
                                            <Box
                                                display="flex"
                                                width="100%"
                                                padding="20px 25px"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Box display="flex" gap="20px" alignItems="center">
                                                    <svg
                                                        width="73"
                                                        height="73"
                                                        viewBox="0 0 73 73"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <circle cx="36.5" cy="36.5" r="36.5" fill="#4A7AF2" fill-opacity="0.1" />
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M23.131 49.1642V31.7734H25.8166V49.1642H23.131Z" fill="white" />
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M19.1056 53.0625H54.9137V55.6711H19.1056V53.0625Z" fill="white" />
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M20.8944 47.8477H53.1217V50.4563H20.8944V47.8477Z" fill="white" />
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M35.6603 49.1642V31.7734H38.3459V49.1642H35.6603Z" fill="white" />
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M48.1958 49.1681V31.7773H50.8814V49.1681H48.1958Z" fill="white" />
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M37.0064 21.1826L23.3623 30.4597H50.6506L37.0064 21.1826ZM36.0824 18.6267C36.6373 18.2494 37.3756 18.2494 37.9305 18.6267L54.9827 30.2209C56.2732 31.0985 55.634 33.0683 54.0586 33.0683H19.9544C18.3789 33.0683 17.7396 31.0985 19.0303 30.2209L36.0824 18.6267Z" fill="white" />
                                                    </svg>
                                                    <Box>
                                                        <Typography variant="h5" className="text-white">
                                                           {account.bankAccountType} - {getDisplayName(account)}
                                                        </Typography>
                                                        <Typography variant="body2" className="text-white">
                                                            {getAccountType(account)} Account |{' '}
                                                            <span style={{ color: '#4A7AF2', fontSize: '14px' }}>
                                                                ****{account.accountLast4}
                                                            </span>
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Box>
                            ))}
                        </RadioGroup>
                    ) : (
                        isAeroPayUser && (
                            <Typography className="text-danger" variant="h6">
                                No Linked Accounts
                            </Typography>
                        )
                    )}
                </Box>

                {/* Pending Accounts */}
                {pendingAccounts.length > 0 && (
                    <Box width='100%'>
                        <Typography variant='h5' mb='20px' className='text-white'>
                            Pending Accounts
                        </Typography>
                        <Box display='flex' style={{ gap: '20px' }}>
                            {pendingAccounts.map((account: Account) => (
                                <Box width='49%' key={account.id}>
                                    <Card sx={{ height: "113px", width: '100% !important' }} className='card'>
                                        <Box display='flex' width='100%' padding='20px 25px' alignItems='center' justifyContent='space-between'>
                                            <Box display='flex' gap='20px' alignItems='center'>
                                                <svg width="73" height="73" viewBox="0 0 73 73" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="36.5" cy="36.5" r="36.5" fill="#4A7AF2" fill-opacity="0.1" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M23.131 49.1642V31.7734H25.8166V49.1642H23.131Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M19.1056 53.0625H54.9137V55.6711H19.1056V53.0625Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M20.8944 47.8477H53.1217V50.4563H20.8944V47.8477Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M35.6603 49.1642V31.7734H38.3459V49.1642H35.6603Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M48.1958 49.1681V31.7773H50.8814V49.1681H48.1958Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M37.0064 21.1826L23.3623 30.4597H50.6506L37.0064 21.1826ZM36.0824 18.6267C36.6373 18.2494 37.3756 18.2494 37.9305 18.6267L54.9827 30.2209C56.2732 31.0985 55.634 33.0683 54.0586 33.0683H19.9544C18.3789 33.0683 17.7396 31.0985 19.0303 30.2209L36.0824 18.6267Z" fill="white" />
                                                </svg>
                                                <Box>
                                                    <Typography variant='h5' className='text-white'>
                                                        {getDisplayName(account)}
                                                    </Typography>
                                                    <Typography variant='h6' sx={{ color: '#4A7AF2' }}>
                                                        {getAccountType(account)} Account |{' '}
                                                        <span style={{ color: '#4A7AF2', fontSize: '14px' }}>
                                                            ****{account.accountLast4}
                                                        </span>
                                                    </Typography>
                                                    <Typography variant='body2' sx={{ color: '#FFFFFF80' }}>
                                                        Status: Pending Activation
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {/* {account.status !== "Cancelled" ? (
                                                <Button
                                                    sx={{
                                                        bgcolor: "#FF2A2A",
                                                        borderRadius: '50px',
                                                        textTransform: 'none',
                                                        padding: '10px 20px',
                                                        '&:hover': { bgcolor: "#FF2A2A" },
                                                        minWidth: '160px'
                                                    }}
                                                    className='text-white'
                                                    onClick={() => onUnlinkAccount(account.id)}
                                                    disabled={unlinkingAccountId === account.id}
                                                >
                                                    {unlinkingAccountId === account.id ? (
                                                        <CircularProgress size={24} sx={{ color: '#ffffff !important' }} />
                                                    ) : (
                                                        'Unlink Account'
                                                    )}
                                                </Button>
                                            ) : (
                                                <Button
                                                    sx={{
                                                        bgcolor: "#4A7AF2",
                                                        borderRadius: '50px',
                                                        textTransform: 'none',
                                                        padding: '10px 20px',
                                                        '&:hover': { bgcolor: "#3A6AE1" }
                                                    }}
                                                    className='text-white'
                                                    onClick={() => open()}
                                                    disabled={!ready || loading}
                                                >
                                                    Link Account
                                                </Button>
                                            )} */}
                                        </Box>
                                    </Card>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Cancelled Accounts */}
                {cancelledAccounts.length > 0 && (
                    <Box width='100%'>
                        <Typography variant='h5' mt='50px' mb='20px' className='text-white'>
                            Cancelled Accounts
                        </Typography>
                        <Box display='flex' width='100%' flexWrap='wrap' style={{ gap: '20px' }}>
                            {cancelledAccounts.map((account: Account) => (
                                <Box width='49%' key={account.id}>
                                    <Card sx={{ height: "113px", width: '100% !important' }} className='card'>
                                        <Box display='flex' width='100%' padding='20px 25px' alignItems='center' justifyContent='space-between'>
                                            <Box display='flex' gap='20px' alignItems='center'>
                                                <svg width="73" height="73" viewBox="0 0 73 73" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="36.5" cy="36.5" r="36.5" fill="#4A7AF2" fill-opacity="0.1" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M23.131 49.1642V31.7734H25.8166V49.1642H23.131Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M19.1056 53.0625H54.9137V55.6711H19.1056V53.0625Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M20.8944 47.8477H53.1217V50.4563H20.8944V47.8477Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M35.6603 49.1642V31.7734H38.3459V49.1642H35.6603Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M48.1958 49.1681V31.7773H50.8814V49.1681H48.1958Z" fill="white" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M37.0064 21.1826L23.3623 30.4597H50.6506L37.0064 21.1826ZM36.0824 18.6267C36.6373 18.2494 37.3756 18.2494 37.9305 18.6267L54.9827 30.2209C56.2732 31.0985 55.634 33.0683 54.0586 33.0683H19.9544C18.3789 33.0683 17.7396 31.0985 19.0303 30.2209L36.0824 18.6267Z" fill="white" />
                                                </svg>
                                                <Box>
                                                    <Typography variant='h5' className='text-white'>
                                                        {getDisplayName(account)}
                                                    </Typography>
                                                    <Typography variant='h6' sx={{ color: '#4A7AF2' }}>
                                                        {getAccountType(account)} Account |{' '}
                                                        <span style={{ color: '#4A7AF2', fontSize: '14px' }}>
                                                            ****{account.accountLast4}
                                                        </span>
                                                    </Typography>
                                                    <Typography variant='body2' sx={{ color: '#FF2A2A80' }}>
                                                        Status: Cancelled
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {/* {accountType === 'stock' ? (
                                                <Button
                                                    sx={{
                                                        bgcolor: "#4A7AF2",
                                                        borderRadius: '50px',
                                                        textTransform: 'none',
                                                        padding: '10px 20px',
                                                        '&:hover': { bgcolor: "#3A6AE1" }
                                                    }}
                                                    className='text-white'
                                                    onClick={() => open()}
                                                    disabled={!ready || loading}
                                                >
                                                    Link Account
                                                </Button>
                                            ) : (
                                                <Button
                                                    sx={{
                                                        bgcolor: "#4A7AF2",
                                                        borderRadius: '50px',
                                                        textTransform: 'none',
                                                        padding: '10px 20px',
                                                        '&:hover': { bgcolor: "#3A6AE1" }
                                                    }}
                                                    className='text-white'
                                                    onClick={() => setShowManualBankModal(true)}
                                                    disabled={loading}
                                                >
                                                    Link Account
                                                </Button>
                                            )} */}
                                        </Box>
                                    </Card>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Footer
            <Box className="footer" width="100%">
                <Divider sx={{ border: '1px solid #FFFFFF33' }} />
                <Typography my="20px" className="text-white" textAlign="center">
                    Securities Products and Services are provided by Wolfpack Brokerage Services Inc. Member FINRA | SIPC.
                </Typography>
            </Box> */}

            {/* Success/Error Popup */}
            {showPopup && (
                <Successpopup
                    Heading={heading}
                    Success={success}
                    onClose={closePopup}
                    Message={msg}
                    showTryAgain={heading === 'Error' && !success}
                    onTryAgain={() => {
                        setShowPopup(false);
                        startBankLink();
                    }}
                />
            )}

            {/* OTP Verification Dialog */}
            <OtpVerificationDialog
                open={needsOtpVerification}
                otp={otp}
                isVerifying={isVerifyingOtp}
                onOtpChange={setOtp}
                onVerify={handleOtpVerification}
                onCancel={cancelOtpVerification}
            />

            {/* AeroSync Widget Modal */}
            <AeroSyncWidgetModal
                open={isWidgetOpen}
                // widgetUrl={widgetUrl}
                isLinking={isLinking}
                onClose={closeWidget}
            />
        </Box>
    );
    // return (
    //     <Box
    //         sx={{ minHeight: 'calc(100vh - 71px - 143px )', display: 'flex', flexDirection: 'column' }}
    //         className="bg-dak"
    //     >
    //         <Box sx={{ padding: '30px', flexGrow: 1 }}>
    //             <Typography className="text-secondary" sx={{ marginBottom: '20px' }}>
    //                 <span onClick={() => navigation(-1)} style={{ color: '#4A7AF2', cursor: 'pointer' }}>
    //                     More Options{' '}
    //                 </span>{' '}
    //                 / Link Bank Accounts
    //             </Typography>
    //         </Box>
    //         <Box
    //             sx={{
    //                 flexGrow: 1,
    //                 display: 'flex',
    //                 alignItems: 'center',
    //                 justifyContent: 'center',
    //                 padding: '30px',
    //             }}
    //         >
    //             <Box
    //                 sx={{
    //                     width: '100%',
    //                     background: 'linear-gradient(145deg, #1a0e08 0%, #1c1108 35%, #110d1c 100%)',
    //                     borderRadius: '20px',
    //                     padding: '52px 40px 44px',
    //                     display: 'flex',
    //                     flexDirection: 'column',
    //                     alignItems: 'center',
    //                     position: 'relative',
    //                     overflow: 'hidden',
    //                 }}
    //             >
    //                 {/* BG glow blobs */}
    //                 <Box sx={{ position: 'absolute', top: -80, right: -80, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle, #f2674a14 0%, transparent 70%)', pointerEvents: 'none' }} />
    //                 <Box sx={{ position: 'absolute', bottom: -60, left: -60, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, #4a7af210 0%, transparent 70%)', pointerEvents: 'none' }} />
    //                 <Box sx={{ position: 'absolute', top: 40, right: 80, width: 3, height: 3, borderRadius: '50%', background: '#f2674a50' }} />
    //                 <Box sx={{ position: 'absolute', top: 80, left: 60, width: 2, height: 2, borderRadius: '50%', background: '#dfdd4e40' }} />
    //                 <Box sx={{ position: 'absolute', bottom: 70, right: 100, width: 3, height: 3, borderRadius: '50%', background: '#4a7af240' }} />
    //                 <Box sx={{ position: 'absolute', bottom: 50, left: 80, width: 2, height: 2, borderRadius: '50%', background: '#f2674a30' }} />

    //                 {/* Floating icon cluster */}
    //                 <Box sx={{ position: 'relative', width: 170, height: 150, mb: '30px' }}>

    //                     {/* Orbit rings */}
    //                     <Box sx={{
    //                         position: 'absolute', left: '50%', top: '50%',
    //                         transform: 'translate(-50%, -50%)',
    //                         width: 140, height: 140, borderRadius: '50%',
    //                         border: '1px dashed #f2674a20',
    //                         animation: 'spinSlow 20s linear infinite',
    //                         '@keyframes spinSlow': { from: { transform: 'translate(-50%,-50%) rotate(0deg)' }, to: { transform: 'translate(-50%,-50%) rotate(360deg)' } },
    //                     }} />
    //                     <Box sx={{
    //                         position: 'absolute', left: '50%', top: '50%',
    //                         transform: 'translate(-50%, -50%)',
    //                         width: 110, height: 110, borderRadius: '50%',
    //                         border: '1px dashed #4a7af218',
    //                         animation: 'spinRev 14s linear infinite',
    //                         '@keyframes spinRev': { from: { transform: 'translate(-50%,-50%) rotate(0deg)' }, to: { transform: 'translate(-50%,-50%) rotate(-360deg)' } },
    //                     }} />

    //                     {/* Top — Card icon (orange/primary) */}
    //                     <Box sx={{
    //                         position: 'absolute', left: '50%', top: 4,
    //                         transform: 'translateX(-50%)',
    //                         animation: 'floatB 3.6s ease-in-out infinite',
    //                         '@keyframes floatB': { '0%,100%': { transform: 'translateX(-50%) translateY(0)' }, '50%': { transform: 'translateX(-50%) translateY(-12px)' } },
    //                     }}>
    //                         <Box sx={{ width: 48, height: 48, borderRadius: '13px', background: 'linear-gradient(135deg, #f2674a, #f4856c)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #f2674a40' }}>
    //                             <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    //                                 <rect x="2" y="5" width="20" height="14" rx="3" stroke="#fff" strokeWidth="1.6" />
    //                                 <path d="M2 10h20" stroke="#fff" strokeWidth="1.6" />
    //                                 <rect x="5" y="14" width="4" height="2" rx="1" fill="#fff" />
    //                             </svg>
    //                         </Box>
    //                     </Box>

    //                     {/* Left — Shield icon (blue/secondary) */}
    //                     <Box sx={{
    //                         position: 'absolute', left: 2, top: '50%',
    //                         animation: 'floatA 4s ease-in-out infinite .4s',
    //                         '@keyframes floatA': { '0%,100%': { transform: 'translateY(-50%)' }, '50%': { transform: 'translateY(calc(-50% - 9px))' } },
    //                     }}>
    //                         <Box sx={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #4a7af2, #6c91f4)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #4a7af240' }}>
    //                             <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    //                                 <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
    //                                 <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    //                             </svg>
    //                         </Box>
    //                     </Box>

    //                     {/* Right — Clock icon (yellow/accent) */}
    //                     <Box sx={{
    //                         position: 'absolute', right: 2, top: '50%',
    //                         animation: 'floatC 3.9s ease-in-out infinite .8s',
    //                         '@keyframes floatC': { '0%,100%': { transform: 'translateY(-50%)' }, '50%': { transform: 'translateY(calc(-50% - 7px))' } },
    //                     }}>
    //                         <Box sx={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #c9b800, #dfdd4e)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #dfdd4e40' }}>
    //                             <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    //                                 <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2Z" stroke="#2c2800" strokeWidth="1.6" />
    //                                 <path d="M12 6v6l4 2" stroke="#2c2800" strokeWidth="1.6" strokeLinecap="round" />
    //                             </svg>
    //                         </Box>
    //                     </Box>

    //                     {/* Bottom — Gift icon (success green) */}
    //                     <Box sx={{
    //                         position: 'absolute', bottom: 2, left: '50%',
    //                         animation: 'floatB2 4.4s ease-in-out infinite 1.2s',
    //                         '@keyframes floatB2': { '0%,100%': { transform: 'translateX(-50%) translateY(0)' }, '50%': { transform: 'translateX(-50%) translateY(-12px)' } },
    //                     }}>
    //                         <Box sx={{ width: 42, height: 42, borderRadius: '11px', background: '#4edf6d1a', border: '1px solid #4edf6d40', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    //                             <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    //                                 <path d="M20 12V22H4V12" stroke="#4edf6d" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    //                                 <path d="M22 7H2v5h20V7Z" stroke="#4edf6d" strokeWidth="1.6" strokeLinejoin="round" />
    //                                 <path d="M12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7ZM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7Z" stroke="#4edf6d" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    //                             </svg>
    //                         </Box>
    //                     </Box>

    //                     {/* Center — Bank icon */}
    //                     <Box sx={{
    //                         position: 'absolute', left: '50%', top: '50%',
    //                         width: 56, height: 56, borderRadius: '50%',
    //                         background: 'linear-gradient(145deg, #1e1208, #261608)',
    //                         border: '1.5px solid #f2674a40',
    //                         display: 'flex', alignItems: 'center', justifyContent: 'center',
    //                         zIndex: 2,
    //                         animation: 'pulseCenter 2.8s ease-in-out infinite',
    //                         '@keyframes pulseCenter': {
    //                             '0%,100%': { opacity: 0.85, transform: 'translate(-50%,-50%) scale(1)' },
    //                             '50%': { opacity: 1, transform: 'translate(-50%,-50%) scale(1.06)' },
    //                         },
    //                     }}>
    //                         <svg width="26" height="26" viewBox="0 0 73 73" fill="none">
    //                             <path fillRule="evenodd" clipRule="evenodd" d="M23.131 49.164V31.773H25.817V49.164H23.131Z" fill="#f2674a" />
    //                             <path fillRule="evenodd" clipRule="evenodd" d="M19.106 53.063H54.914V55.671H19.106V53.063Z" fill="#f2674a" />
    //                             <path fillRule="evenodd" clipRule="evenodd" d="M20.894 47.848H53.122V50.456H20.894V47.848Z" fill="#f2674a" />
    //                             <path fillRule="evenodd" clipRule="evenodd" d="M35.66 49.164V31.773H38.346V49.164H35.66Z" fill="#f2674a" />
    //                             <path fillRule="evenodd" clipRule="evenodd" d="M48.196 49.168V31.777H50.881V49.168H48.196Z" fill="#f2674a" />
    //                             <path fillRule="evenodd" clipRule="evenodd" d="M37.006 21.183L23.362 30.46H50.651L37.006 21.183ZM36.082 18.627C36.637 18.249 37.376 18.249 37.931 18.627L54.983 30.221C56.273 31.099 55.634 33.068 54.059 33.068H19.954C18.379 33.068 17.74 31.099 19.03 30.221L36.082 18.627Z" fill="#fcb118" />
    //                         </svg>
    //                     </Box>
    //                 </Box>

    //                 {/* Badge */}
    //                 <Box sx={{
    //                     display: 'inline-flex', alignItems: 'center', gap: '8px',
    //                     px: '16px', py: '5px', borderRadius: '100px',
    //                     background: 'linear-gradient(90deg, #f2674a18, #fcb11815)',
    //                     border: '1px solid #f2674a35', mb: 2,
    //                 }}>
    //                     <Box sx={{
    //                         width: 6, height: 6, borderRadius: '50%', bgcolor: '#f2674a',
    //                         animation: 'blinkDot 1.5s ease-in-out infinite',
    //                         '@keyframes blinkDot': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.2 } },
    //                     }} />
    //                     <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: '#f4856c' }}>
    //                         In Development
    //                     </Typography>
    //                 </Box>

    //                 {/* Heading — animated gradient using your brand colors */}
    //                 <Typography variant="h3" sx={{
    //                     mb: '6px', fontWeight: 700,
    //                     background: 'linear-gradient(90deg, #f2674a, #fcb118 50%, #dfdd4e)',
    //                     backgroundSize: '200% 200%',
    //                     animation: 'gradShift 4s ease infinite',
    //                     '@keyframes gradShift': {
    //                         '0%': { backgroundPosition: '0% 50%' },
    //                         '50%': { backgroundPosition: '100% 50%' },
    //                         '100%': { backgroundPosition: '0% 50%' },
    //                     },
    //                     WebkitBackgroundClip: 'text',
    //                     WebkitTextFillColor: 'transparent',
    //                     backgroundClip: 'text',
    //                     letterSpacing: '-0.03em',
    //                     textAlign: 'center',
    //                 }}>
    //                     Coming Soon
    //                 </Typography>

    //                 <Typography sx={{ mb: '6px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'center', color: '#f2674a70' }}>
    //                     Link Bank Accounts
    //                 </Typography>

    //                 <Typography variant="body2" sx={{ mt: '12px', mb: '28px', color: '#a3a3a3', textAlign: 'center', maxWidth: 370, lineHeight: 1.85 }}>
    //                     We're building a secure, seamless way to connect your bank. Fund your Wolfpack account and start investing in just a few taps.
    //                 </Typography>

    //                 {/* Feature pills */}
    //                 <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', mb: '30px' }}>
    //                     {[
    //                         { color: '#f2674a', bg: '#f2674a0d', border: '#f2674a25', label: 'Instant transfers' },
    //                         { color: '#4a7af2', bg: '#4a7af20d', border: '#4a7af225', label: 'Bank-grade security' },
    //                         { color: '#dfdd4e', bg: '#dfdd4e0d', border: '#dfdd4e25', label: 'Multiple accounts' },
    //                     ].map(({ color, bg, border, label }) => (
    //                         <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '7px', px: '15px', py: '8px', borderRadius: '10px', background: bg, border: `1px solid ${border}` }}>
    //                             <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
    //                             <Typography sx={{ fontSize: '12px', color: '#a3a3a3' }}>{label}</Typography>
    //                         </Box>
    //                     ))}
    //                 </Box>

    //                 {/* Stay tuned divider */}
    //                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', maxWidth: 280 }}>
    //                     <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, grey)' }} />
    //                     <Typography sx={{ fontSize: '14px', color: 'grey', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>stay tuned</Typography>
    //                     <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, grey, transparent)' }} />
    //                 </Box>
    //             </Box>
    //         </Box>
    //         <Box className="footer" width="100%">
    //             <Divider sx={{ border: '1px solid #FFFFFF33' }} />
    //             <Typography my="20px" className="text-white" textAlign="center">
    //                 Securities Products and Services are provided by Wolfpack Brokerage Services Inc. Member FINRA | SIPC.
    //             </Typography>
    //         </Box>
    //     </Box>
    // );
}

export default LinkAccountSimplified;